import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { InlineTitleField } from './InlineTitleField'
import { exercisesApi } from '@/api/exercises'
import { programsApi, type Program, type ProgramTheme, type ProgramThemeElement } from '@/api/programs'
import { getErrorMessage } from '@/utils/error'

/** Тема вместе с разделом, в котором она сейчас лежит */
type FlatTheme = ProgramTheme & { section_id: number | null }

type Drag =
  | { kind: 'section'; id: number }
  | { kind: 'theme'; id: number }
  | { kind: 'element'; themeId: number; id: number }
  | null

/** Курсор ниже середины строки — вставка после неё, выше — перед */
const dropsAfter = (event: React.DragEvent): boolean => {
  const box = (event.currentTarget as HTMLElement).getBoundingClientRect()

  return event.clientY > box.top + box.height / 2
}

const moveTo = <T extends { id: number }>(list: T[], id: number, index: number): T[] => {
  const from = list.findIndex((item) => item.id === id)
  if (from < 0) return list

  const next = [...list]
  const [moved] = next.splice(from, 1)
  const target = Math.max(0, Math.min(next.length, from < index ? index - 1 : index))
  next.splice(target, 0, moved)

  return next
}

/**
 * Структура программы: разделы, темы и блоки тем.
 *
 * Порядок разделов не хранится — он выводится из позиций тем, сквозных по всей
 * программе (см. модель ProgramSection). Поэтому перетаскивание раздела здесь —
 * перенос всего его отрезка тем, а список тем всегда нормализуется в непрерывные
 * отрезки: сервер отклоняет чередование, а в унаследованных данных оно бывает.
 *
 * Порядок сохраняется в drop, а не в dragend. При переносе темы в другой раздел
 * React пересоздаёт строку под другой карточкой, узел-источник уходит из
 * документа, и dragend до делегированного слушателя уже не доходит — сохранение,
 * повешенное на него, не выполнялось вовсе. Drop приходит на живую цель.
 */
export function ProgramStructureTab({
  program,
  readOnly,
  onOpenElement,
  onAddElement,
  onOpenExercise,
  onDeleteTheme,
  onElementMenu,
  onChanged,
  onError,
}: {
  program: Program
  readOnly: boolean
  onOpenElement: (theme: ProgramTheme, element: ProgramThemeElement) => void
  onAddElement: (theme: ProgramTheme) => void
  /** Открыть карточку задания: диалог живёт на странице вместе с диалогом блока */
  onOpenExercise: (theme: ProgramTheme, exerciseId: number) => void
  onDeleteTheme: (theme: ProgramTheme) => void
  onElementMenu: (anchor: HTMLElement, theme: ProgramTheme, element: ProgramThemeElement) => void
  onChanged: () => void
  onError: (message: string) => void
}) {
  const { t } = useTranslation()

  const [expanded, setExpanded] = useState<number[]>([])

  // Блок-задание открывается конструктором задания, а не диалогом теории:
  // содержимое задания живёт в отдельной сущности, и в теле элемента его нет

  // Меню задания живёт здесь, а не на странице: там оно предлагает действия
  // блока — правку тела и удаление элемента, — а заданию нужны свои.
  // Элемент держим целиком: «Убрать из темы» снимает именно строку темы, и по
  // одному exercise_id её не найти.
  const [exerciseMenu, setExerciseMenu] = useState<{
    anchor: HTMLElement
    theme: FlatTheme
    element: ProgramThemeElement
    exerciseId: number
  } | null>(null)
  const [drag, setDrag] = useState<Drag>(null)
  const [pending, setPending] = useState(0)
  // Двойник счётчика в ссылке — см. runClick: состояние обработчику кнопки
  // достаётся из прошлого рендера и от двойного клика не спасает
  const pendingRef = useRef(0)

  const [themes, setThemes] = useState<FlatTheme[]>([])

  /**
   * Витринные задания темы (is_system).
   *
   * В элементе темы этого флага нет — там только тип, статус и заголовок, —
   * а знать его нужно до открытия карточки: витринное задание учитель править
   * не может, и без бейджа он вводил текст, а на «Сохранить» получал 403 и
   * терял всё. Берём флаг из уже существующего списка заданий темы: один
   * запрос на раскрытие темы, а не на строку.
   */
  const [systemExerciseIds, setSystemExerciseIds] = useState<number[]>([])
  // Ключ пробы: тема плюс её состав заданий. По одному id темы задание, только
  // что поставленное из банка, оставалось бы без бейджа до перезагрузки
  // страницы — а это ровно тот случай, ради которого бейдж и нужен. Состав
  // отсортирован: перетаскивание блоков менять его не должно
  const probedKeysRef = useRef<string[]>([])

  const probeKeys = useMemo(
    () =>
      expanded.flatMap((themeId) => {
        const theme = themes.find((item) => item.id === themeId)
        if (!theme) return []

        const ids = (theme.elements ?? [])
          .flatMap((element) => (element.type === 'exercise' && element.exercise_id != null ? [element.exercise_id] : []))
          .sort((a, b) => a - b)

        return ids.length === 0 ? [] : [`${themeId}:${ids.join(',')}`]
      }),
    [expanded, themes],
  )

  useEffect(() => {
    const pending = probeKeys.filter((key) => !probedKeysRef.current.includes(key))
    if (pending.length === 0) return

    probedKeysRef.current = [...probedKeysRef.current, ...pending]

    // Бейдж — подсказка, а не условие работы структуры: недоступность списка
    // не должна ломать раскрытие темы
    void Promise.all(
      pending.map((key) => exercisesApi.listByTheme(Number(key.split(':')[0])).catch(() => [])),
    ).then((lists) => {
      const found = lists.flat().flatMap((exercise) => (exercise.is_system ? [exercise.id] : []))
      if (found.length === 0) return

      setSystemExerciseIds((prev) => [...new Set([...prev, ...found])])
    })
  }, [probeKeys])

  const isSystemExercise = (element: ProgramThemeElement): boolean =>
    element.exercise_id != null && systemExerciseIds.includes(element.exercise_id)

  // Единственный писатель порядка: ссылка обновляется синхронно со стейтом.
  // Пассивный useEffect отставал бы на шаг — dragover флашится асинхронно, а
  // drop читает порядок сразу.
  const themesRef = useRef<FlatTheme[]>([])
  const dragRef = useRef<Drag>(null)

  const applyThemes = useCallback((next: FlatTheme[]) => {
    themesRef.current = next
    setThemes(next)
  }, [])

  const setDragState = useCallback((next: Drag) => {
    dragRef.current = next
    setDrag(next)
  }, [])

  /**
   * Серверный порядок, приведённый к непрерывным отрезкам: разделы идут в
   * порядке своей первой темы. Отправлять чередование нельзя — сервер отклонит
   * его даже на действии, к разрыву не относящемся.
   */
  const serverThemes = useMemo<FlatTheme[]>(() => {
    const flat = [...(program.themes ?? [])]
      .sort((a, b) => a.position - b.position)
      .map((theme) => ({ ...theme, section_id: theme.section_id ?? null }))

    const order: Array<number | null> = []
    for (const theme of flat) {
      if (!order.includes(theme.section_id)) order.push(theme.section_id)
    }

    return order.flatMap((sectionId) => flat.filter((theme) => theme.section_id === sectionId))
  }, [program.themes])

  useEffect(() => {
    if (dragRef.current !== null || pending > 0) return
    applyThemes(serverThemes)
  }, [serverThemes, pending, applyThemes, drag])

  /** Разделы в порядке первой своей темы; пустые — в конец */
  const groups = useMemo(() => {
    const order: Array<number | null> = []
    for (const theme of themes) {
      if (!order.includes(theme.section_id)) order.push(theme.section_id)
    }
    for (const section of program.sections ?? []) {
      if (!order.includes(section.id)) order.push(section.id)
    }

    return order.map((sectionId) => ({
      id: sectionId,
      title:
        sectionId === null
          ? t('programs.noSection')
          : ((program.sections ?? []).find((section) => section.id === sectionId)?.title ?? ''),
      themes: themes.filter((theme) => theme.section_id === sectionId),
    }))
  }, [themes, program.sections, t])

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      // Счётчик, а не флаг: окончание одного запроса не должно разблокировать
      // синхронизацию, пока второй ещё в полёте
      pendingRef.current += 1
      setPending((count) => count + 1)
      try {
        await action()
        onChanged()
      } catch (err) {
        onError(getErrorMessage(err) || t('common.error'))
      } finally {
        pendingRef.current -= 1
        setPending((count) => count - 1)
      }
    },
    [onChanged, onError, t],
  )

  /**
   * Действие по клику, уходящее в сеть.
   *
   * Проверка идёт по ссылке, а не по состоянию: `pending` обработчик читает из
   * замыкания своего рендера, и два быстрых клика по «Добавить раздел»
   * проходили оба до перерисовки — учитель получал два раздела. Ссылка растёт
   * синхронно, ещё до первого await.
   *
   * Сохранение порядка и правку названий пропускаем через `run` напрямую:
   * молча отменить их из-за чужого запроса значит потерять перетаскивание или
   * набранный заголовок.
   */
  const runClick = useCallback(
    (action: () => Promise<unknown>) => {
      if (pendingRef.current > 0) return

      void run(action)
    },
    [run],
  )

  const commitThemes = useCallback(() => {
    const ordered = themesRef.current
    const key = (list: FlatTheme[]) => list.map((theme) => `${theme.id}:${theme.section_id}`).join(',')
    if (key(serverThemes) === key(ordered)) return

    void run(() =>
      programsApi.reorderThemes(
        program.id,
        ordered.map((theme) => ({ id: theme.id, section_id: theme.section_id })),
      ),
    )
  }, [program.id, run, serverThemes])

  const commitElements = useCallback(
    (themeId: number) => {
      const current = themesRef.current.find((theme) => theme.id === themeId)?.elements ?? []
      const before = (serverThemes.find((theme) => theme.id === themeId)?.elements ?? [])
        .map((element) => element.id)
        .join(',')
      if (before === current.map((element) => element.id).join(',')) return

      void run(() =>
        programsApi.reorderElements(
          themeId,
          current.map((element) => ({ id: element.id })),
        ),
      )
    },
    [run, serverThemes],
  )

  /** Бросок в любой точке структуры сохраняет то, что уже видно на экране */
  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    const active = dragRef.current
    setDragState(null)
    if (active === null) return

    if (active.kind === 'element') commitElements(active.themeId)
    else commitThemes()
  }

  const handleStart = (payload: Exclude<Drag, null>) => (event: React.DragEvent) => {
    setDragState(payload)
    event.dataTransfer.effectAllowed = 'move'
    // Firefox не начинает перетаскивание без setData
    event.dataTransfer.setData('text/plain', String(payload.id))
    event.stopPropagation()
  }

  /**
   * Отмена для брошенного мимо структуры. При переносе между разделами dragend
   * не приходит вовсе — там всё уже решил drop, а drag к этому моменту сброшен.
   */
  const handleEnd = () => {
    if (dragRef.current === null) return
    setDragState(null)
    applyThemes(serverThemes)
  }

  const onThemeOver = (target: FlatTheme) => (event: React.DragEvent) => {
    const active = dragRef.current
    if (active?.kind !== 'theme') return

    // preventDefault на каждом dragover, включая наведение на саму строку:
    // иначе после перестановки зона под курсором становится запретной и drop
    // не наступает
    event.preventDefault()
    event.stopPropagation()
    if (active.id === target.id) return

    const list = themesRef.current
    const targetIndex = list.findIndex((theme) => theme.id === target.id)
    if (targetIndex < 0) return

    const next = moveTo(list, active.id, targetIndex + (dropsAfter(event) ? 1 : 0))
    const movedIndex = next.findIndex((theme) => theme.id === active.id)
    if (movedIndex < 0) return

    next[movedIndex] = { ...next[movedIndex], section_id: target.section_id }
    applyThemes(next)
  }

  /** Карточка раздела: бросок мимо строк — «в конец этого раздела» */
  const onSectionOver = (sectionId: number | null) => (event: React.DragEvent) => {
    const active = dragRef.current
    if (active === null) return

    event.preventDefault()

    if (active.kind === 'theme') {
      const list = themesRef.current
      const moved = list.find((theme) => theme.id === active.id)
      if (!moved) return

      const sections = list.map((theme) => theme.section_id)
      // Уже последняя в этом разделе — переставлять нечего
      if (moved.section_id === sectionId && list[sections.lastIndexOf(sectionId)]?.id === active.id) return

      const rest = list.filter((theme) => theme.id !== active.id)
      const lastOfSection = rest.map((theme) => theme.section_id).lastIndexOf(sectionId)
      const at = lastOfSection < 0 ? rest.length : lastOfSection + 1

      applyThemes([...rest.slice(0, at), { ...moved, section_id: sectionId }, ...rest.slice(at)])

      return
    }

    if (active.kind !== 'section' || active.id === sectionId) return

    const list = themesRef.current
    const moving = list.filter((theme) => theme.section_id === active.id)
    if (moving.length === 0) return

    const rest = list.filter((theme) => theme.section_id !== active.id)
    const targetIndex = rest.findIndex((theme) => theme.section_id === sectionId)
    // У пустого раздела своей позиции нет — вставать «перед» ним некуда
    if (targetIndex < 0) return

    applyThemes([...rest.slice(0, targetIndex), ...moving, ...rest.slice(targetIndex)])
  }

  /** Блоки: бросок мимо строк остаётся в этой же теме, порядок не меняя */
  const onElementsOver = (themeId: number) => (event: React.DragEvent) => {
    const active = dragRef.current
    if (active?.kind !== 'element' || active.themeId !== themeId) return

    event.preventDefault()
    event.stopPropagation()
  }

  const onElementOver = (themeId: number, target: ProgramThemeElement) => (event: React.DragEvent) => {
    const active = dragRef.current
    if (active?.kind !== 'element' || active.themeId !== themeId) return

    event.preventDefault()
    event.stopPropagation()
    if (active.id === target.id) return

    const theme = themesRef.current.find((item) => item.id === themeId)
    const elements = theme?.elements ?? []
    const targetIndex = elements.findIndex((element) => element.id === target.id)
    if (targetIndex < 0) return

    const ordered = moveTo(elements, active.id, targetIndex + (dropsAfter(event) ? 1 : 0))
    applyThemes(themesRef.current.map((item) => (item.id === themeId ? { ...item, elements: ordered } : item)))
  }

  const dragHandle = (payload: Exclude<Drag, null>) => ({
    draggable: !readOnly,
    onDragStart: handleStart(payload),
    onDragEnd: handleEnd,
  })

  const isDragged = (kind: 'section' | 'theme' | 'element', id: number) =>
    drag !== null && drag.kind === kind && drag.id === id

  const openExercise = (theme: FlatTheme, exerciseId: number) => {
    onOpenExercise(theme, exerciseId)
  }

  const openElementRow = (theme: FlatTheme, element: ProgramThemeElement) => {
    if (element.type !== 'exercise') {
      onOpenElement(theme, element)

      return
    }

    if (element.exercise_id != null) void openExercise(theme, element.exercise_id)
  }

  const addSection = () => runClick(() => programsApi.createSection(program.id, t('programs.newSectionTitle')))

  const addTheme = (sectionId: number | null) =>
    runClick(() => programsApi.createTheme(program.id, { title: t('programs.newThemeTitle'), section_id: sectionId }))

  return (
    <Box
      onDragOver={(event) => {
        if (dragRef.current !== null) event.preventDefault()
      }}
      onDrop={handleDrop}
    >
      {groups.map((group) => (
        <Card
          key={group.id ?? 'none'}
          sx={{ mb: 2, opacity: group.id !== null && isDragged('section', group.id) ? 0.5 : 1 }}
          onDragOver={onSectionOver(group.id)}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {/* Пустой раздел двигать нечем: его место выводится из тем */}
              {!readOnly && group.id !== null && group.themes.length > 0 && (
                <Box
                  sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab' }}
                  {...dragHandle({ kind: 'section', id: group.id })}
                >
                  <DragIndicatorIcon fontSize="small" />
                </Box>
              )}

              <InlineTitleField
                value={group.title}
                disabled={readOnly || group.id === null}
                placeholder={t('programs.fields.sectionTitle')}
                onSave={(title) => run(() => programsApi.updateSection(program.id, group.id as number, title))}
              />

              {!readOnly && (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    size="small"
                    // Спиннер вместо плюса: погашенная кнопка без него читается
                    // как сломанная, и учитель бьёт по ней повторно
                    startIcon={pending > 0 ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    disabled={pending > 0}
                    onClick={() => addTheme(group.id)}
                  >
                    {t('programs.addTheme')}
                  </Button>
                  {group.id !== null && (
                    <IconButton
                      size="small"
                      disabled={pending > 0}
                      aria-label={t('programs.deleteSection')}
                      onClick={() => runClick(() => programsApi.deleteSection(program.id, group.id as number))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            {group.themes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('programs.sectionEmpty')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {group.themes.map((theme) => (
                  <Box key={theme.id} onDragOver={onThemeOver(theme)}>
                    <ListItem
                      sx={{ opacity: isDragged('theme', theme.id) ? 0.5 : 1 }}
                      secondaryAction={
                        !readOnly && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {/* Вход один: тип блока (в том числе «Задание»)
                              выбирается уже в диалоге — две кнопки заставляли
                              решать до того, как открылась форма */}
                            <Button size="small" onClick={() => onAddElement(theme)}>
                              {t('programs.addElement')}
                            </Button>
                            <IconButton
                              size="small"
                              aria-label={t('programs.deleteTheme')}
                              onClick={() => onDeleteTheme(theme)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )
                      }
                    >
                      {!readOnly && (
                        <Box
                          sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab', mr: 0.5 }}
                          {...dragHandle({ kind: 'theme', id: theme.id })}
                        >
                          <DragIndicatorIcon fontSize="small" />
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        sx={{ mr: 1 }}
                        aria-label={t('programs.structure')}
                        onClick={() =>
                          setExpanded((prev) =>
                            prev.includes(theme.id) ? prev.filter((id) => id !== theme.id) : [...prev, theme.id],
                          )
                        }
                      >
                        {expanded.includes(theme.id) ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                      <ListItemText
                        primary={
                          <InlineTitleField
                            value={theme.title}
                            variant="body1"
                            disabled={readOnly}
                            placeholder={t('programs.fields.themeTitle')}
                            onSave={(title) => run(() => programsApi.updateTheme(program.id, theme.id, { title }))}
                          />
                        }
                        secondary={t('programs.elementsCount', {
                          count: theme.elements?.length ?? theme.elements_count ?? 0,
                        })}
                      />
                    </ListItem>

                    {expanded.includes(theme.id) && (
                      <Box onDragOver={onElementsOver(theme.id)}>
                        <List dense disablePadding sx={{ pl: 6 }}>
                          {(theme.elements ?? []).length === 0 ? (
                            <ListItem>
                              <ListItemText secondary={t('programs.themeEmpty')} />
                            </ListItem>
                          ) : (
                            (theme.elements ?? []).map((element) => (
                              <ListItem
                                key={element.id}
                                disablePadding
                                sx={{ opacity: isDragged('element', element.id) ? 0.5 : 1 }}
                                onDragOver={onElementOver(theme.id, element)}
                                secondaryAction={
                                  !readOnly && (
                                    <IconButton
                                      size="small"
                                      onClick={(event) =>
                                        element.type === 'exercise' && element.exercise_id != null
                                          ? setExerciseMenu({
                                              anchor: event.currentTarget,
                                              theme,
                                              element,
                                              exerciseId: element.exercise_id,
                                            })
                                          : onElementMenu(event.currentTarget, theme, element)
                                      }
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  )
                                }
                              >
                                {!readOnly && (
                                  <Box
                                    sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab', mr: 0.5 }}
                                    {...dragHandle({ kind: 'element', themeId: theme.id, id: element.id })}
                                  >
                                    <DragIndicatorIcon fontSize="small" />
                                  </Box>
                                )}
                                {/* Клик по названию открывает блок: у системной
                                    программы меню действий скрыто, и содержимое
                                    иначе не посмотреть */}
                                <ListItemButton onClick={() => openElementRow(theme, element)}>
                                  <ListItemText
                                    primary={
                                      element.type === 'exercise' ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                          {/* Название весомее бейджей: иначе
                                              строка читается сплошным текстом */}
                                          <Typography component="span" fontWeight={600}>
                                            {element.exercise?.title || element.title || t('programs.elementTypes.exercise')}
                                          </Typography>
                                          {/* variant="outlined": заливка чипа по
                                              умолчанию совпадает с фоном строки,
                                              и тип задания на экране не виден */}
                                          {element.exercise && (
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={t(`exercises.types.${element.exercise.type}.name`)}
                                            />
                                          )}
                                          {/* «Готово» — норма, а не новость:
                                              показываем только то, из-за чего
                                              задание не уедет ученику */}
                                          {element.exercise && element.exercise.status !== 'ready' && (
                                            <Chip
                                              size="small"
                                              color={element.exercise.status === 'draft' ? 'warning' : 'default'}
                                              label={t(`exercises.exerciseStatus.${element.exercise.status}`)}
                                            />
                                          )}
                                          {/* Витринное задание правит платформа:
                                              без бейджа учитель открывал его как
                                              своё и терял текст на 403 */}
                                          {isSystemExercise(element) && (
                                            <Chip
                                              size="small"
                                              color="secondary"
                                              label={t('exercises.bank.systemBadge', 'EasyA')}
                                            />
                                          )}
                                        </Box>
                                      ) : (
                                        element.title ||
                                        (element.type === 'vocabulary'
                                          ? t('programs.elementTypes.vocabulary')
                                          : t('programs.elementTypes.theory'))
                                      )
                                    }
                                    secondary={
                                      element.type === 'vocabulary'
                                        ? t('programs.wordsCount', { count: element.words?.length ?? 0 })
                                        : element.type === 'exercise'
                                          ? null
                                          : t('programs.elementTypes.theory')
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))
                          )}
                        </List>
                      </Box>
                    )}
                  </Box>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      ))}

      <Menu anchorEl={exerciseMenu?.anchor ?? null} open={exerciseMenu !== null} onClose={() => setExerciseMenu(null)}>
        {/* У витринного задания правки нет вовсе: сервер запрещает PATCH, и
            «Редактировать» приводило только к 403 с потерей введённого */}
        <MenuItem
          onClick={() => {
            const target = exerciseMenu
            setExerciseMenu(null)
            if (target) void openExercise(target.theme, target.exerciseId)
          }}
        >
          {exerciseMenu !== null && isSystemExercise(exerciseMenu.element)
            ? t('common.view', 'Посмотреть')
            : t('common.edit')}
        </MenuItem>
        {/* Пункт меню кликается так же, как кнопка: пока запрос в полёте, он
            погашен — иначе учитель успевал заказать две копии подряд */}
        <MenuItem
          disabled={pending > 0}
          onClick={() => {
            const target = exerciseMenu
            setExerciseMenu(null)
            if (target) runClick(() => exercisesApi.duplicate(target.exerciseId, target.theme.id))
          }}
        >
          {t('exercises.theme.duplicate')}
        </MenuItem>
        {/* «Убрать из темы» вместо архивации: архивация — операция банка, она
            снимала задание со ВСЕХ программ и рвала его посреди прохождения у
            учеников. Здесь отвязывается только строка этой темы */}
        <MenuItem
          disabled={pending > 0}
          onClick={() => {
            const target = exerciseMenu
            setExerciseMenu(null)
            if (target) runClick(() => programsApi.deleteElement(target.theme.id, target.element.id))
          }}
        >
          {t('exercises.theme.detach', 'Убрать из темы')}
        </MenuItem>
      </Menu>

      {!readOnly && (
        // Контурная карточка кликается как кнопка, значит и гаснуть должна как
        // кнопка: pointerEvents снимает клик целиком (проверки в обработчике
        // мало — два быстрых нажатия уходили оба), а приглушение и спиннер
        // показывают, что запрос уже идёт
        <Card
          variant="outlined"
          role="button"
          aria-disabled={pending > 0}
          onClick={() => addSection()}
          sx={{
            borderStyle: 'dashed',
            cursor: pending > 0 ? 'default' : 'pointer',
            bgcolor: 'transparent',
            opacity: pending > 0 ? 0.5 : 1,
            pointerEvents: pending > 0 ? 'none' : 'auto',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 3 }}>
            {pending > 0 ? <CircularProgress size={16} /> : <AddIcon fontSize="small" color="action" />}
            <Typography color="text.secondary">{t('programs.addSection')}</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
